import { useCallback, useEffect, useState } from "react";
import { BarChart3, Download, Info } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import api from "../../api/api";
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";
import { Alert, EmptyState, Panel, Stat, apiError, formatDate, useOrgStyles } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";

/* Impact report (Alicia, Oct 16): for an organization's grant reports, how many
   of its students used Pono Pilot and how many jobs they applied to, by
   fiscal year, then per program and cohort. Totals only, never names. Download
   as PDF or Excel. The fiscal year starts in the month set in Settings. Org
   admins only (adminOnly in ORG_PAGES). */

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October",
    "November", "December"];

// (the share who used the app went with Used the app, Oct 30)
//old const pct = (part, whole) => (whole ? `${Math.round((part / whole) * 100)}%` : "–");

// The roster uploads that haven't joined, next to the students who joined (not counted in them)
//old const INVITED_ABOUT = "People uploaded to the roster during the fiscal year who haven't joined yet. They aren't counted in Students who joined or the share who used the app.";
// (added, not uploaded: Add client and the Overview's Assign a license put people on the roster too)
const INVITED_ABOUT = "People added to the roster during the fiscal year who haven't joined yet. They aren't counted in Clients who joined or the other numbers.";

/** The report as rows: Total, then each program and its cohorts. */
function reportRows(report) {
    //old const rows = [{ level: 0, name: "All students", ...report.totals }];
    const rows = [{ level: 0, name: "All clients", ...report.totals }];
    report.programs.forEach((p) => {
        // every number the row has (the counts added Oct 30 too), as the totals row above takes them
        //old rows.push({ level: 1, name: p.archived ? `${p.name} (archived)` : p.name, students: p.students, activeStudents: p.activeStudents, jobsApplied: p.jobsApplied });
        rows.push({ level: 1, ...p, name: p.archived ? `${p.name} (archived)` : p.name });
        //old (p.cohorts || []).forEach((c) => rows.push({ level: 2, name: c.name, students: c.students, activeStudents: c.activeStudents, jobsApplied: c.jobsApplied }));
        (p.cohorts || []).forEach((c) => rows.push({ level: 2, ...c }));
    });
    return rows;
}

export default function OrgImpact() {
    const { orgId, org, isPlatformAdmin, goTo } = useOrgPortal();
    const s = useOrgStyles();
    const [fy, setFy] = useState(null); // null = the current fiscal year
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/org/${orgId}/impact`, { params: fy ? { fy } : {} });
            setReport(res.data);
            setError("");
        } catch (err) {
            setError(apiError(err, "Couldn't load the impact report."));
        } finally {
            setLoading(false);
        }
    }, [orgId, fy]);

    useEffect(() => { load(); }, [load]);

    const period = report ? `FY${report.fiscalYear}: ${formatDate(report.start)} – ${formatDate(report.end)}` : "";
    const fileName = report ? `${report.orgName.replace(/[^\w]+/g, "-").toLowerCase()}-impact-FY${report.fiscalYear}` : "impact";

    const downloadExcel = () => {
        const rows = reportRows(report).map((r) => ({
            "Program / cohort": `${"  ".repeat(Math.max(0, r.level - 1))}${r.name}`,
            //old Students: r.students,
            //old "Students who joined": r.students,
            "Clients who joined": r.students,
            // Logins where Used the app and its share were (Oct 30), then the counts added that day; 0 from an older backend
            //old "Used the app": r.activeStudents,
            //old "Share who used the app": pct(r.activeStudents, r.students),
            Logins: r.logins ?? 0,
            "Jobs applied to": r.jobsApplied,
            "Mock interviews": r.mockInterviews ?? 0,
            "Elevator pitches": r.elevatorPitches ?? 0,
            "Resume analyses": r.resumeAnalyses ?? 0,
            "Resume checklists": r.resumeChecklists ?? 0,
        }));
        const wb = XLSX.utils.book_new();
        const info = [
            ["Organization", report.orgName],
            ["Fiscal year", period],
            ["Created", formatDate(report.generatedOn)],
            ["Invited, not joined yet", report.invited ?? 0],
            [],
            //old ["Students who joined", "Signed up and in the organization during the fiscal year (the share who used the app is out of them)"],
            ["Clients who joined", "Signed up and in the organization during the fiscal year"],
            //old ["Used the app", "Signed in or used a feature (resume analysis, resume checklist, mock interview, elevator pitch, career assessment, job tracker) while in the organization during the fiscal year"],
            ["Logins", "Times clients signed in while in the organization during the fiscal year (staying signed in counts as one login)"],
            //old ["Jobs applied to", "Jobs students marked as applied in their job tracker while in the organization during the fiscal year"],
            ["Jobs applied to", "Jobs clients marked as applied in their job tracker while in the organization during the fiscal year"],
            ["Mock interviews", "Practice interviews clients started while in the organization during the fiscal year"],
            ["Elevator pitches", "Elevator pitch practices clients started while in the organization during the fiscal year"],
            ["Resume analyses", "Resumes clients scored against a job description while in the organization during the fiscal year (runs that failed don't count)"],
            ["Resume checklists", "Resumes clients ran through the Resume Checklist while in the organization during the fiscal year (runs that failed don't count)"],
            ["Invited, not joined yet", INVITED_ABOUT],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Impact");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(info), "About");
        XLSX.writeFile(wb, `${fileName}.xlsx`);
    };

    const downloadPdf = () => {
        // landscape A4 (Alicia, Oct 30), so the eight columns fit across; text uses the width between the 14 mm margins
        //old const doc = new jsPDF();
        const doc = new jsPDF({ orientation: "landscape" });
        const width = doc.internal.pageSize.getWidth() - 28;
        doc.setFontSize(16);
        // a long organization name wraps instead of running off the page
        //old const title = doc.splitTextToSize(`${report.orgName}: impact report`, 182);
        const title = doc.splitTextToSize(`${report.orgName}: impact report`, width);
        doc.text(title, 14, 18);
        const dy = (title.length - 1) * 7;
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`${period} · created ${formatDate(report.generatedOn)} · Pono Pilot`, 14, 25 + dy);
        doc.setTextColor(0);
        // uploaded to the roster this year and not joined: next to the table, not in it
        doc.text(`Invited, not joined yet: ${report.invited ?? 0}`, 14, 32 + dy);
        autoTable(doc, {
            //old startY: 32 + dy,
            startY: 37 + dy,
            //old head: [["Program / cohort", "Students", "Used the app", "Share", "Jobs applied to"]],
            //old head: [["Program / cohort", "Students who joined", "Used the app", "Share", "Jobs applied to"]],
            head: [["Program / cohort", "Clients who joined", "Logins", "Jobs applied to", "Mock interviews", "Elevator pitches",
                "Resume analyses", "Resume checklists"]],
            body: reportRows(report).map((r) => [
                `${"   ".repeat(Math.max(0, r.level - 1))}${r.name}`,
                //old r.students, r.activeStudents, pct(r.activeStudents, r.students), r.jobsApplied,
                r.students, r.logins ?? 0, r.jobsApplied, r.mockInterviews ?? 0, r.elevatorPitches ?? 0, r.resumeAnalyses ?? 0,
                r.resumeChecklists ?? 0,
            ]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [79, 70, 229] },
            didParseCell: (d) => { if (d.section === "body" && d.row.index === 0) d.cell.styles.fontStyle = "bold"; },
        });
        doc.setFontSize(8);
        doc.setTextColor(110);
        //old doc.text(doc.splitTextToSize(
        const notes = doc.splitTextToSize(
            //old "Used the app: signed in or used a feature (resume analysis, resume checklist, mock interview, elevator pitch, "
            //old "Students who joined: signed up and in the organization during the fiscal year. "
            "Clients who joined: signed up and in the organization during the fiscal year. "
            //old + "Used the app: signed in or used a feature (resume analysis, resume checklist, mock interview, elevator pitch, "
            //old + "career assessment, job tracker) while in the organization during the fiscal year. Jobs applied to: jobs students "
            + "Logins: times clients signed in while in the organization during the fiscal year; staying signed in counts as one "
            + "login. Jobs applied to: jobs clients "
            //old + "marked as applied in their job tracker while in the organization during the fiscal year. Totals only; no student "
            //old + "marked as applied in their job tracker while in the organization during the fiscal year. Invited, not joined yet: "
            + "marked as applied in their job tracker while in the organization during the fiscal year. Mock interviews: practice "
            + "interviews clients started during that time. Elevator pitches: pitch practices they started. Resume analyses: resumes "
            + "they scored against a job description. Resume checklists: resumes they ran through the Resume Checklist. Resume runs "
            + "that failed don't count. Invited, not joined yet: "
            //old + "people uploaded to the roster during the fiscal year who haven't joined yet, not counted in the other numbers. "
            + "people added to the roster during the fiscal year who haven't joined yet, not counted in the other numbers. "
            //old + "Totals only; no student "
            + "Totals only; no client "
            //old + "names are included.", 180), 14, doc.lastAutoTable.finalY + 8);
            + "names are included.", width);
        // under the table, or at the top of a new page when they don't fit under it (a long list of programs and cohorts)
        let y = doc.lastAutoTable.finalY + 8;
        if (y + doc.getTextDimensions(notes).h > doc.internal.pageSize.getHeight() - 14) {
            doc.addPage();
            y = 18;
        }
        doc.text(notes, 14, y);
        doc.save(`${fileName}.pdf`);
    };

    const rows = report ? reportRows(report) : [];
    return (
        <div className="space-y-6">
            <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${s.subPanel}`}>
                <Info size={18} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />
                <p className={s.body}>
                    {/* old How many of your students used Pono Pilot and how many jobs they applied to, by fiscal year, */}
                    How many of your clients used Pono Pilot and how many jobs they applied to, by fiscal year,
                    for your grant reports. Only totals, never names. Your fiscal year starts in{" "}
                    {MONTHS[(report?.fiscalYearStartMonth || org.FiscalYearStartMonth || 1) - 1]}
                    {!isPlatformAdmin && <>; change it in <button type="button" className={s.btnLink} onClick={() => goTo("settings")}>Settings</button></>}.
                </p>
            </div>
            <Alert onClose={() => setError("")}>{error}</Alert>

            <Panel
                title={<span className="inline-flex items-center gap-2"><BarChart3 size={18} /> Impact report</span>}
                actions={report && (
                    <div className="flex flex-wrap gap-2">
                        <button type="button" className={s.btnSecondary} onClick={downloadPdf}><Download size={14} /> PDF</button>
                        <button type="button" className={s.btnSecondary} onClick={downloadExcel}><Download size={14} /> Excel</button>
                    </div>
                )}
            >
                <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <label htmlFor="oi-fy" className={`text-sm font-semibold ${s.text}`}>Fiscal year</label>
                    <select id="oi-fy" className={`${s.input} sm:w-40`} value={fy || report?.fiscalYear || ""}
                            onChange={(e) => setFy(Number(e.target.value))} disabled={!report}>
                        {(report?.fiscalYears || []).map((y) => <option key={y} value={y}>FY{y}</option>)}
                    </select>
                    {report && <span className={`text-sm ${s.muted}`}>{formatDate(report.start)} – {formatDate(report.end)}</span>}
                </div>

                {loading && !report ? (
                    <>
                        {/* old <div className="mb-5 grid gap-3 sm:grid-cols-3"> */}
                        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            {/* old {[0, 1, 2].map((i) => <Skeleton key={i} height={92} borderRadius={16} */}
                            {/* old {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={92} borderRadius={16} */}
                            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} height={92} borderRadius={16}
                                                             baseColor={s.dark ? "#334155" : "#e2e8f0"} highlightColor={s.dark ? "#475569" : "#f1f5f9"} />)}
                        </div>
                        {/* old <InlineTableSkeleton isDarkMode={s.dark} rows={3} cols={4} bordered={false} /> */}
                        <InlineTableSkeleton isDarkMode={s.dark} rows={3} cols={8} bordered={false} />
                    </>
                ) : report && (
                    <>
                        {/* old <div className={`mb-6 grid gap-3 sm:grid-cols-3 ${loading ? "opacity-60" : ""}`}> */}
                        <div className={`mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 ${loading ? "opacity-60" : ""}`}>
                            {/* old <Stat label="Students" value={report.totals.students} hint="In your organization during the year" /> */}
                            {/* the students counted are the ones who joined; the roster's invited ones are their own number */}
                            {/* old <Stat label="Students who joined" value={report.totals.students} hint="Signed up and in your organization during the year" /> */}
                            <Stat label="Clients who joined" value={report.totals.students} hint="Signed up and in your organization during the year" />
                            {/* Logins where Used the app was (Alicia, Oct 30): sign-ins, a count, so no share; 0 from an older backend */}
                            {/* old <Stat label="Used the app" value={report.totals.activeStudents} */}
                            <Stat label="Logins" value={report.totals.logins ?? 0}
                                  //old hint={`${pct(report.totals.activeStudents, report.totals.students)} of your students`} />
                                  //old hint={`${pct(report.totals.activeStudents, report.totals.students)} of the students who joined`} />
                                  hint="Times your clients signed in" />
                            <Stat label="Jobs applied to" value={report.totals.jobsApplied} hint="From their job trackers" />
                            {/* old <Stat label="Invited, not joined yet" value={report.invited ?? 0} hint="Uploaded to your roster during the year" /> */}
                            {/* (added, not uploaded: Add client and the Overview's Assign a license count too) */}
                            <Stat label="Invited, not joined yet" value={report.invited ?? 0} hint="Added to your roster during the year" />
                            {/* what clients used (Oct 30), named as the app names it */}
                            <Stat label="Mock interviews" value={report.totals.mockInterviews ?? 0} hint="Practice interviews your clients started" />
                            <Stat label="Elevator pitches" value={report.totals.elevatorPitches ?? 0} hint="Pitch practices your clients started" />
                            <Stat label="Resume analyses" value={report.totals.resumeAnalyses ?? 0} hint="Resumes scored against a job description" />
                            <Stat label="Resume checklists" value={report.totals.resumeChecklists ?? 0} hint="Resumes run through the Resume Checklist" />
                        </div>
                        {report.totals.students === 0 ? (
                            //old <EmptyState icon={BarChart3} title="No students that year"
                            //old <EmptyState icon={BarChart3} title="No students joined that year"
                            <EmptyState icon={BarChart3} title="No clients joined that year"
                                        //old body="Pick another fiscal year, or come back once students have joined." />
                                        body="Pick another fiscal year, or come back once clients have joined." />
                        ) : (
                            <div className={`relative overflow-x-auto ${loading ? "opacity-60" : ""}`}>
                                <table className="w-full">
                                    <thead>
                                        <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                            <th className={s.th}>Program / cohort</th>
                                            {/* old <th className={`${s.th} text-right`}>Students</th> */}
                                            {/* old <th className={`${s.th} text-right`}>Students who joined</th> */}
                                            <th className={`${s.th} text-right`}>Clients who joined</th>
                                            {/* old <th className={`${s.th} text-right`}>Used the app</th> */}
                                            <th className={`${s.th} text-right`}>Logins</th>
                                            <th className={`${s.th} text-right`}>Jobs applied to</th>
                                            <th className={`${s.th} text-right`}>Mock interviews</th>
                                            <th className={`${s.th} text-right`}>Elevator pitches</th>
                                            <th className={`${s.th} text-right`}>Resume analyses</th>
                                            <th className={`${s.th} text-right`}>Resume checklists</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr key={i} className={`border-b last:border-0 ${s.row}`}>
                                                <td className={s.td}>
                                                    {/* old <span className={`${r.level === 0 ? `font-bold ${s.text}` : r.level === 1 ? `font-semibold ${s.text}` : `pl-5 ${s.body}`}`}>{r.name}</span> */}
                                                    {/* one line on a computer; on a phone a cohort's name keeps its indent on every line it wraps to, so it doesn't read as a program */}
                                                    <span className={`inline-block lg:whitespace-nowrap ${r.level === 0 ? `font-bold ${s.text}` : r.level === 1 ? `font-semibold ${s.text}` : `pl-5 ${s.body}`}`}>{r.name}</span>
                                                </td>
                                                <td className={`${s.td} text-right tabular-nums`}>{r.students}</td>
                                                <td className={`${s.td} text-right tabular-nums`}>
                                                    {/* old {r.activeStudents} <span className={`text-xs ${s.muted}`}>({pct(r.activeStudents, r.students)})</span> */}
                                                    {r.logins ?? 0}
                                                </td>
                                                <td className={`${s.td} text-right tabular-nums`}>{r.jobsApplied}</td>
                                                <td className={`${s.td} text-right tabular-nums`}>{r.mockInterviews ?? 0}</td>
                                                <td className={`${s.td} text-right tabular-nums`}>{r.elevatorPitches ?? 0}</td>
                                                <td className={`${s.td} text-right tabular-nums`}>{r.resumeAnalyses ?? 0}</td>
                                                <td className={`${s.td} text-right tabular-nums`}>{r.resumeChecklists ?? 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <p className={`mt-4 text-xs ${s.muted}`}>
                            {/* old Students who joined: signed up and in your organization during the year. */}
                            Clients who joined: signed up and in your organization during the year.
                            {/* old Used the app: signed in or used a feature (resume analysis, resume checklist, mock interview, elevator pitch, */}
                            {/* old career assessment, job tracker) while in your organization during the year. Jobs applied to: jobs students */}
                            {" "}Logins: times your clients signed in while in your organization during the year; staying signed in counts
                            as one login. Jobs applied to: jobs clients
                            marked as applied in their job tracker during that time.
                            Mock interviews: practice interviews your clients started during that time. Elevator pitches: pitch practices
                            they started. Resume analyses: resumes they scored against a job description. Resume checklists: resumes they
                            ran through the Resume Checklist. Resume runs that failed don't count.
                            {/* old Invited, not joined yet: people uploaded to your roster during the year who haven't joined yet, not counted in the other numbers. */}
                            {" "}Invited, not joined yet: people added to your roster during the year who haven't joined yet, not counted in the other numbers.
                            {report.programs.some((p) => p.cohorts === null) && " Programs that don't show cohorts list only their totals."}
                        </p>
                    </>
                )}
            </Panel>
        </div>
    );
}
